import { useVuexDispatch, useVuexGetters } from '@scfe-common/micro-frontend-utils';
import * as React from 'react';

import { $gt } from '@/common/appCtx';

import { TimeRangeType } from './api';
import GroupThroughOverview from './components/group-throughput-overview';
import LastTenHoursGroupThroughput from './components/last-ten-hours-group-throughput';
import WorkstationOperatorPerformance from './components/workstation-operator-performance';
import { CacheType } from './const';
import useRefreshInterval from './hooks/use-refresh-interval';
import styles from './index.module.less';
import useDropdownListStore from './store/use-dropdown-list';
import useLastHourOperatorPerformanceStore from './store/use-last-hour-operator-performance';
import useWorkstationPerformanceStore from './store/use-last-hour-workstation-peformance';
import { useLastTenHoursGroupThroughputStore } from './store/use-last-ten-hours-group-throughput';
import useLastTenHoursOperatorPerformanceStore from './store/use-last-ten-hours-operator-performance';
import useLastTenHoursWorkstationPerformanceStore from './store/use-last-ten-hours-workstation-performance';
import useWorkstationOverviewStore from './store/use-workstation-overview';
import { generateIntervals, getWorkstationDashboardCache } from './utils/common';

const WorkstationDashboard = () => {
  const vuexDispatch = useVuexDispatch();
  const getGroupListAsync = useWorkstationOverviewStore.use.getGroupListAsync();
  const groupDropdownListAsync = useDropdownListStore.use.getLastHourGroupDropdownListAsync();
  const getThroughputForm = useLastTenHoursGroupThroughputStore.use.getThroughputForm();
  const getGroupLastTenHoursThroughputAsync = useLastTenHoursGroupThroughputStore.use.getGroupLastTenHoursThroughputAsync();
  const getTargetGroup = useWorkstationOverviewStore.use.getTargetGroup();
  const getThroughputGroupDropdownListAsync = useDropdownListStore.use.getLastTenHoursGroupDropdownListAsync();
  const getLastHourWorkstationDropdownListAsync = useDropdownListStore.use.getLastHourWorkstationDropdownListAsync();
  const getLastTenHoursWorkstationDropdownListAsync = useDropdownListStore.use.getLastTenHoursWorkstationDropdownListAsync();
  const getLastHourOperatorDropdownListAsync = useDropdownListStore.use.getLastHourOperatorDropdownListAsync();
  const getLastTenHoursOperatorDropdownListAsync = useDropdownListStore.use.getLastTenHoursOperatorDropdownListAsync();
  const getWorkstationPerformanceLastHourAsync = useWorkstationPerformanceStore.use.getWorkstationPerformanceLastHourAsync();
  const getWorkstationPerformanceLastTenHoursAsync = useLastTenHoursWorkstationPerformanceStore.use.getWorkstationPerformanceLastTenHoursAsync();
  const getOperatorPerformanceLastHourAsync = useLastHourOperatorPerformanceStore.use.getOperatorPerformanceLastHourAsync();
  const getOperatorPerformanceLastTenHoursAsync = useLastTenHoursOperatorPerformanceStore.use.getOperatorPerformanceLastTenHoursAsync();
  const setTimeIntervals = useWorkstationOverviewStore.use.setTimeIntervals();
  const workstationDashboardMockBeginTime = useVuexGetters('workforceGlobalDataOps/workstationDashboardMockBeginTime');

  const getLastTenHoursThroughputData = React.useCallback(() => {
    const lastTenHoursThroughputCache = getWorkstationDashboardCache(CacheType.Last10HoursGroupThroughput);
    if (lastTenHoursThroughputCache) {
      getThroughputForm()?.setFieldsValue(lastTenHoursThroughputCache);
    }
    const workstationGroupIds = getThroughputForm()?.getFieldValue('workstation_group_id_list') || [];
    getGroupLastTenHoursThroughputAsync({ workstation_group_id_list: [getTargetGroup()?.groupId, ...workstationGroupIds] });
    getThroughputGroupDropdownListAsync({ time_range_type: TimeRangeType.Last10Hours });
    groupDropdownListAsync({ time_range_type: TimeRangeType.Last10Hours });
  }, [getThroughputForm, getGroupLastTenHoursThroughputAsync, getTargetGroup, getThroughputGroupDropdownListAsync, groupDropdownListAsync]);

  const getWorkstationPerformanceData = React.useCallback(() => {
    const workstation_group_id = getTargetGroup()?.groupId;
    // workstation dropdown list
    getLastHourWorkstationDropdownListAsync({ workstation_group_id });
    getLastTenHoursWorkstationDropdownListAsync({ workstation_group_id });
    // operator dropdown list
    getLastHourOperatorDropdownListAsync({ workstation_group_id });
    getLastTenHoursOperatorDropdownListAsync({ workstation_group_id });
    // workstation performance
    getWorkstationPerformanceLastHourAsync({ workstation_group_id });
    getWorkstationPerformanceLastTenHoursAsync({ workstation_group_id });
    // operator performance
    getOperatorPerformanceLastHourAsync({ workstation_group_id });
    getOperatorPerformanceLastTenHoursAsync({ workstation_group_id });
  }, [getTargetGroup, getLastHourWorkstationDropdownListAsync, getLastTenHoursWorkstationDropdownListAsync,
    getLastHourOperatorDropdownListAsync, getLastTenHoursOperatorDropdownListAsync, getWorkstationPerformanceLastHourAsync,
     getWorkstationPerformanceLastTenHoursAsync, getOperatorPerformanceLastHourAsync, getOperatorPerformanceLastTenHoursAsync]);

  // it is necessary to use layout effect to ensure the overview form is set before subscribing
  React.useLayoutEffect(() => {
    const unsubscribe = useWorkstationOverviewStore.subscribe(
      state => state.overviewForm,
      (overviewForm, prevOverviewForm) => {
        if (overviewForm && overviewForm !== prevOverviewForm) {
          getGroupListAsync();
        }
      }
    );
    return () => unsubscribe();
  }, [getGroupListAsync]);

  // refresh when group list is loaded and target group is set
  React.useEffect(() => {
    const unsubscribe = useWorkstationOverviewStore.subscribe(
      state => state.targetGroup,
      (targetGroup, prevTargetGroup) => {
        if (targetGroup && !!targetGroup.groupId && targetGroup !== prevTargetGroup) {
          const timeIntervals = generateIntervals(10, workstationDashboardMockBeginTime);
          setTimeIntervals(timeIntervals);
          getLastTenHoursThroughputData();
          getWorkstationPerformanceData();
        }
      }
    );
    return () => unsubscribe();
  }, [getLastTenHoursThroughputData, getWorkstationPerformanceData, setTimeIntervals, workstationDashboardMockBeginTime]);

  const refresh = React.useCallback(() => {
    const timeIntervals = generateIntervals(10, workstationDashboardMockBeginTime);
    setTimeIntervals(timeIntervals);
    getGroupListAsync();
  }, [getGroupListAsync, setTimeIntervals, workstationDashboardMockBeginTime]);

  useRefreshInterval(refresh);

  React.useLayoutEffect(() => {
    vuexDispatch('sharedBreadcrumb/updateBreadcrumb', [
      { link: '', title: $gt('Dashboard') },
      { link: '/workforceops/workstation-dashboard/overview', title: $gt('Workstation Dashboard') },
    ]);
  }, [vuexDispatch]);

  return (
    <div className={styles.overview}>
      <GroupThroughOverview />
      <LastTenHoursGroupThroughput />
      <WorkstationOperatorPerformance />
    </div>
  );
};

export default WorkstationDashboard;
